import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ChevronLeft, ChevronRight, Download, Printer, TrendingUp, Eye, BarChart3, Package, Building2, CreditCard, LineChart } from 'lucide-react';
import { formatCurrency, formatPercent, cn } from '../lib/utils';
import { exportIncomeStatementToExcel } from './ExportExcelButton';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList, Cell, ComposedChart, Line, Area
} from 'recharts';

const CHART_COLORS = {
  revenue: '#3B82F6',
  ebitda: '#10B981',
  netIncome: '#8B5CF6',
  cogs: '#EF4444',
  opex: '#F59E0B',
  depreciation: '#94A3B8',
  interest: '#EC4899',
  balance: '#0EA5E9',
  principal: '#10B981'
};

const PRODUCT_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#0EA5E9', '#6366F1', '#14B8A6'];
const INVESTMENT_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#0EA5E9', '#6366F1', '#14B8A6'];
const CATEGORY_COLORS = {
  'Personal': '#3B82F6',
  'Oficina y Renta': '#10B981',
  'IT e Infraestructura': '#94A3B8',
  'Marketing': '#F59E0B',
  'Servicios': '#8B5CF6',
  'Otros': '#EC4899'
};

// Waffle Chart Component for composition
const WaffleChart = ({ data, title }) => {
  if (!data || data.length === 0) return null;
  
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const sortedData = [...data].sort((a, b) => b.value - a.value);
  
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-muted-foreground">{title}</h4>
      <div className="grid grid-cols-2 gap-3">
        {sortedData.map((item, idx) => {
          const percentage = (item.value / total) * 100;
          const filledSquares = Math.round(percentage / 4); // 25 squares = 100%
          
          return (
            <div key={item.name} className="bg-secondary/30 rounded-lg p-3 border border-border/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground truncate max-w-[100px]">
                  {item.name}
                </span>
                <span className="text-lg font-bold" style={{ color: item.color }}>
                  {percentage.toFixed(0)}%
                </span>
              </div>
              <div className="grid grid-cols-5 gap-0.5">
                {Array.from({ length: 25 }).map((_, i) => (
                  <div 
                    key={i}
                    className="aspect-square rounded-sm"
                    style={{ 
                      backgroundColor: i < filledSquares ? item.color : '#374151',
                      opacity: i < filledSquares ? 1 : 0.3
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const IncomeStatementPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, fetchProject, getCalculation } = useProject();
  const { formatTableValue, formatChartValue, formatVolumeValue, showLabels } = useChartPreferences();
  
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailType, setDetailType] = useState(null);
  const [costFilter, setCostFilter] = useState('all');
  const [perfFilter, setPerfFilter] = useState('all');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!currentProject || currentProject.id !== projectId) {
          await fetchProject(projectId);
        }
        const data = await getCalculation(projectId, 'income-statement');
        setIncomeStatement(data);
      } catch (err) {
        console.error('Error loading income statement:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId, currentProject, fetchProject, getCalculation]);

  const goToNext = () => navigate(`/app/project/${projectId}/cash-flow`);
  const goToPrev = () => navigate(`/app/project/${projectId}/fixed-expenses`);

  const currency = currentProject?.specifications?.currency || 'MXN';
  const years = incomeStatement?.years || [];
  const products = currentProject?.products || [];
  const fixedExpenses = currentProject?.fixed_expenses || [];
  const inflation = currentProject?.financing?.inflation_rate || 5;
  const numYears = currentProject?.specifications?.project_life_years || 5;

  const formatValue = (value) => formatTableValue(value, currency);

  const getMargin = (value, revenue) => {
    if (!revenue || revenue === 0) return 0;
    return (value / revenue) * 100;
  };

  const calculateCagr = (firstValue, lastValue, periods) => {
    if (!firstValue || firstValue <= 0 || !lastValue || periods <= 0) return null;
    return (Math.pow(Math.abs(lastValue) / Math.abs(firstValue), 1 / periods) - 1) * 100;
  };

  const getCagr = (key) => {
    const firstYear = years[0];
    const lastYear = years[years.length - 1];
    if (!firstYear || !lastYear) return null;
    return calculateCagr(firstYear[key], lastYear[key], years.length - 1);
  };

  // ==================== CALCULATION FUNCTIONS (mirrors backend logic) ====================
  const daysPerYear = currentProject?.specifications?.operates_seven_days !== false ? 365 : 312;
  const daysPerMonth = daysPerYear / 12;
  const projectOperatingMonths = currentProject?.operating_months || Array(numYears).fill(12);
  const investments = currentProject?.investments || [];
  const financing = currentProject?.financing || {};
  const debtAmount = financing.debt_amount || 0;
  const costOfDebt = financing.cost_of_debt || 0;
  const monthlyAmortization = financing.monthly_amortization !== false;

  // Calculate product projections for a given year (mirrors backend)
  const calculateProductYear = (product, year) => {
    const baseVolume = product.base_volume || 0;
    const opMonths = projectOperatingMonths[year - 1] || 12;
    const annualBase = product.input_frequency === 'daily' 
      ? baseVolume * daysPerMonth * opMonths 
      : baseVolume * opMonths;
    
    // Calculate volume
    let volume = annualBase;
    if (product.growth_type === 'percentage' || product.growth_type === 'growth') {
      volume = annualBase * Math.pow(1 + (product.growth_percentage || 0) / 100, year - 1);
    } else if (product.growth_type === 'decline') {
      volume = annualBase * Math.pow(1 - (product.growth_percentage || 0) / 100, year - 1);
    } else if (product.growth_type === 'manual') {
      const manualVol = product.manual_volumes?.[String(year)] || product.manual_volumes?.[year];
      if (manualVol != null) {
        volume = product.input_frequency === 'daily' ? manualVol * daysPerMonth * opMonths : manualVol * opMonths;
      }
    }
    
    // Calculate price
    const basePrice = product.unit_price || 0;
    let price = basePrice;
    const priceGrowth = product.price_growth_type || 'inflation';
    if (priceGrowth === 'inflation') {
      price = basePrice * Math.pow(1 + inflation / 100, year - 1);
    } else {
      const manual = product.manual_prices?.[String(year)] || {};
      price = manual.price || basePrice;
    }
    
    // Calculate cost
    const baseCost = product.unit_cost || 0;
    let cost = baseCost;
    if (product.costs && product.costs.length > 0) {
      cost = 0;
      for (const ci of product.costs) {
        if (ci.growth_type === 'manual' && ci.manual_amounts) {
          const yrAmount = ci.manual_amounts[String(year)];
          cost += yrAmount !== undefined && yrAmount !== null ? yrAmount : (ci.amount || 0);
        } else {
          cost += (ci.amount || 0) * Math.pow(1 + inflation / 100, year - 1);
        }
      }
    } else {
      const costGrowth = product.cost_growth_type || 'inflation';
      if (costGrowth === 'inflation') {
        cost = baseCost * Math.pow(1 + inflation / 100, year - 1);
      } else {
        const manual = product.manual_prices?.[String(year)] || {};
        cost = manual.cost || baseCost;
      }
    }
    
    return { volume, price, cost, revenue: volume * price, cogs: volume * cost };
  };

  // Calculate amortization schedule (mirrors backend)
  const calculateAmortizationSchedule = () => {
    const schedule = [];
    if (debtAmount <= 0) {
      for (let year = 1; year <= numYears; year++) {
        schedule.push({ year, beginning_balance: 0, principal: 0, interest: 0, ending_balance: 0 });
      }
      return schedule;
    }
    
    const annualRate = costOfDebt / 100;
    let balance = debtAmount;
    
    if (monthlyAmortization) {
      const annualPrincipal = debtAmount / numYears;
      for (let year = 1; year <= numYears; year++) {
        const beginningBalance = balance;
        const interest = balance * annualRate;
        const principal = annualPrincipal;
        balance = Math.max(0, balance - principal);
        schedule.push({
          year,
          beginning_balance: beginningBalance,
          principal,
          interest,
          payment: principal + interest,
          ending_balance: balance
        });
      }
    } else {
      for (let year = 1; year <= numYears; year++) {
        const beginningBalance = balance;
        const interest = balance * annualRate;
        const principal = year === numYears ? debtAmount : 0;
        if (year === numYears) balance = 0;
        schedule.push({
          year,
          beginning_balance: beginningBalance,
          principal,
          interest,
          payment: principal + interest,
          ending_balance: balance
        });
      }
    }
    return schedule;
  };

  const amortizationSchedule = calculateAmortizationSchedule();

  // Get volume data for all years by product
  const getVolumeDataAllYears = () => {
    if (!products || products.length === 0) return { tableData: [], chartData: [], waffleData: [] };
    
    const tableData = products.map((p, idx) => {
      const row = { name: p.name, color: PRODUCT_COLORS[idx % PRODUCT_COLORS.length] };
      let totalVolume = 0;
      
      for (let year = 1; year <= numYears; year++) {
        const calc = calculateProductYear(p, year);
        row[`year${year}`] = calc.volume;
        totalVolume += calc.volume;
      }
      row.total = totalVolume;
      row.avg = totalVolume / numYears;
      return row;
    });
    
    const chartData = Array.from({ length: numYears }, (_, i) => {
      const year = i + 1;
      const data = { year: `Año ${year}` };
      products.forEach((p) => {
        const calc = calculateProductYear(p, year);
        data[p.name] = calc.volume;
      });
      data._total = Object.keys(data).filter(k => k !== 'year' && k !== '_total').reduce((s, k) => s + (data[k] || 0), 0);
      return data;
    });
    
    const waffleData = tableData.map(p => ({
      name: p.name,
      value: p.avg,
      color: p.color
    }));
    
    return { tableData, chartData, waffleData };
  };

  // Get revenue data for all years by product
  const getRevenueDataAllYears = () => {
    if (!products || products.length === 0) return { tableData: [], chartData: [], waffleData: [] };
    
    const tableData = products.map((p, idx) => {
      const row = { name: p.name, color: PRODUCT_COLORS[idx % PRODUCT_COLORS.length] };
      let totalRevenue = 0;
      
      for (let year = 1; year <= numYears; year++) {
        const calc = calculateProductYear(p, year);
        row[`year${year}`] = calc.revenue;
        totalRevenue += calc.revenue;
      }
      row.total = totalRevenue;
      row.avg = totalRevenue / numYears;
      return row;
    });
    
    const chartData = Array.from({ length: numYears }, (_, i) => {
      const year = i + 1;
      const data = { year: `Año ${year}` };
      products.forEach((p) => {
        const calc = calculateProductYear(p, year);
        data[p.name] = calc.revenue;
      });
      data._total = Object.keys(data).filter(k => k !== 'year' && k !== '_total').reduce((s, k) => s + (data[k] || 0), 0);
      return data;
    });
    
    const waffleData = tableData.map(p => ({
      name: p.name,
      value: p.avg,
      color: p.color
    }));
    
    return { tableData, chartData, waffleData };
  };

  // Get COGS data for all years by product
  const getCogsDataAllYears = () => {
    if (!products || products.length === 0) return { tableData: [], chartData: [], waffleData: [] };
    
    const tableData = products.map((p, idx) => {
      const row = { name: p.name, color: PRODUCT_COLORS[idx % PRODUCT_COLORS.length] };
      let totalCogs = 0;
      
      for (let year = 1; year <= numYears; year++) {
        const calc = calculateProductYear(p, year);
        row[`year${year}`] = calc.cogs;
        totalCogs += calc.cogs;
      }
      row.total = totalCogs;
      row.avg = totalCogs / numYears;
      return row;
    });
    
    const chartData = Array.from({ length: numYears }, (_, i) => {
      const year = i + 1;
      const data = { year: `Año ${year}` };
      products.forEach((p) => {
        const calc = calculateProductYear(p, year);
        data[p.name] = calc.cogs;
      });
      data._total = Object.keys(data).filter(k => k !== 'year' && k !== '_total').reduce((s, k) => s + (data[k] || 0), 0);
      return data;
    });
    
    const waffleData = tableData.map(p => ({
      name: p.name,
      value: p.avg,
      color: p.color
    }));
    
    return { tableData, chartData, waffleData };
  };

  // Get OPEX data for all years by category
  const getOpexDataAllYears = () => {
    if (!fixedExpenses || fixedExpenses.length === 0) return { tableData: [], chartData: [], waffleData: [] };
    
    const categories = ['Personal', 'Oficina y Renta', 'IT e Infraestructura', 'Marketing', 'Servicios', 'Otros'];
    
    const tableData = categories.map((cat) => {
      const row = { name: cat, color: CATEGORY_COLORS[cat] || '#6B7280' };
      let totalAmount = 0;
      
      for (let year = 1; year <= numYears; year++) {
        let yearAmount = 0;
        
        fixedExpenses.filter(e => e.category === cat).forEach(e => {
          const startYear = e.start_year || 1;
          if (year >= startYear) {
            const yearsSinceStart = year - startYear;
            let amount = (e.monthly_amount || 0) * 12;
            
            if (e.growth_type === 'inflation') {
              amount = amount * Math.pow(1 + inflation / 100, yearsSinceStart);
            } else if (e.growth_type === 'fixed') {
              // stays the same
            } else if (e.growth_type === 'manual' && e.manual_amounts?.[year]) {
              amount = e.manual_amounts[year];
            }
            yearAmount += amount;
          }
        });
        
        row[`year${year}`] = yearAmount;
        totalAmount += yearAmount;
      }
      row.total = totalAmount;
      row.avg = totalAmount / numYears;
      return row;
    }).filter(row => row.total > 0);
    
    const chartData = Array.from({ length: numYears }, (_, i) => {
      const year = i + 1;
      const data = { year: `Año ${year}` };
      
      tableData.forEach(cat => {
        data[cat.name] = cat[`year${year}`];
      });
      data._total = Object.keys(data).filter(k => k !== 'year' && k !== '_total').reduce((s, k) => s + (data[k] || 0), 0);
      return data;
    });
    
    const waffleData = tableData.map(cat => ({
      name: cat.name,
      value: cat.avg,
      color: cat.color
    }));
    
    return { tableData, chartData, waffleData };
  };

  // Get Depreciation data for all years by investment
  const getDepreciationDataAllYears = () => {
    if (!investments || investments.length === 0) return { tableData: [], chartData: [], waffleData: [] };
    
    const tableData = investments.map((inv, idx) => {
      const row = { name: inv.concept, color: INVESTMENT_COLORS[idx % INVESTMENT_COLORS.length] };
      let totalDep = 0;
      const depYears = inv.depreciation_years || numYears;
      const annualDep = inv.amount / depYears;
      
      for (let year = 1; year <= numYears; year++) {
        const dep = year <= depYears ? annualDep : 0;
        row[`year${year}`] = dep;
        totalDep += dep;
      }
      row.total = totalDep;
      row.avg = totalDep / numYears;
      row.amount = inv.amount;
      row.depYears = depYears;
      return row;
    });
    
    const chartData = Array.from({ length: numYears }, (_, i) => {
      const year = i + 1;
      const data = { year: `Año ${year}` };
      investments.forEach((inv) => {
        const depYears = inv.depreciation_years || numYears;
        const annualDep = inv.amount / depYears;
        data[inv.concept] = year <= depYears ? annualDep : 0;
      });
      data._total = Object.keys(data).filter(k => k !== 'year' && k !== '_total').reduce((s, k) => s + (data[k] || 0), 0);
      return data;
    });
    
    const waffleData = tableData.map(inv => ({
      name: inv.name,
      value: inv.amount,
      color: inv.color
    }));
    
    return { tableData, chartData, waffleData };
  };

  // Get Interest data with amortization details
  const getInterestDataAllYears = () => {
    if (debtAmount <= 0) return { tableData: [], chartData: [], waffleData: [], amortizationData: [] };
    
    const tableData = [{
      name: 'Crédito Bancario',
      color: CHART_COLORS.interest,
      debtAmount,
      rate: costOfDebt,
      ...amortizationSchedule.reduce((acc, row) => {
        acc[`year${row.year}`] = row.interest;
        return acc;
      }, {}),
      total: amortizationSchedule.reduce((sum, row) => sum + row.interest, 0),
      avg: amortizationSchedule.reduce((sum, row) => sum + row.interest, 0) / numYears
    }];
    
    const chartData = amortizationSchedule.map(row => ({
      year: `Año ${row.year}`,
      'Intereses': row.interest,
      'Capital': row.principal
    }));
    
    const amortizationData = amortizationSchedule.map(row => ({
      year: `Año ${row.year}`,
      'Saldo Insoluto': row.beginning_balance,
      'Intereses': row.interest,
      'Capital': row.principal
    }));
    
    const waffleData = [
      { name: 'Intereses Totales', value: tableData[0].total, color: CHART_COLORS.interest },
      { name: 'Capital (Deuda)', value: debtAmount, color: CHART_COLORS.principal }
    ];
    
    return { tableData, chartData, waffleData, amortizationData };
  };

  // Get Gross Profit (Utilidad Bruta) data for all years by product
  const getGrossProfitDataAllYears = () => {
    if (!products || products.length === 0) return { tableData: [], chartData: [], waffleData: [] };
    
    const tableData = products.map((p, idx) => {
      const row = { name: p.name, color: PRODUCT_COLORS[idx % PRODUCT_COLORS.length] };
      let totalRevenue = 0;
      let totalCogs = 0;
      let totalProfit = 0;
      
      for (let year = 1; year <= numYears; year++) {
        const calc = calculateProductYear(p, year);
        const profit = calc.revenue - calc.cogs;
        row[`revenue_year${year}`] = calc.revenue;
        row[`cogs_year${year}`] = calc.cogs;
        row[`profit_year${year}`] = profit;
        row[`margin_year${year}`] = calc.revenue > 0 ? (profit / calc.revenue) * 100 : 0;
        totalRevenue += calc.revenue;
        totalCogs += calc.cogs;
        totalProfit += profit;
      }
      row.totalRevenue = totalRevenue;
      row.totalCogs = totalCogs;
      row.totalProfit = totalProfit;
      row.avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
      return row;
    });
    
    const chartData = Array.from({ length: numYears }, (_, i) => {
      const year = i + 1;
      const data = { year: `Año ${year}` };
      products.forEach((p) => {
        const calc = calculateProductYear(p, year);
        data[p.name] = calc.revenue - calc.cogs;
      });
      data._total = Object.keys(data).filter(k => k !== 'year' && k !== '_total').reduce((s, k) => s + (data[k] || 0), 0);
      return data;
    });
    
    const waffleData = tableData.map(p => ({
      name: p.name,
      value: p.totalProfit,
      color: p.color
    }));
    
    return { tableData, chartData, waffleData };
  };

  const openDetail = (type) => {
    setDetailType(type);
    setDetailModalOpen(true);
  };

  const getDetailTitle = () => {
    switch (detailType) {
      case 'volume': return 'Análisis de Volumen de Ventas';
      case 'revenue': return 'Análisis de Ingresos por Producto';
      case 'cogs': return 'Análisis de Costo de Ventas';
      case 'grossProfit': return 'Análisis de Utilidad Bruta por Producto';
      case 'opex': return 'Análisis de Gastos Operativos';
      case 'depreciation': return 'Análisis de Depreciación por Activo';
      case 'interest': return 'Análisis de Costo Financiero';
      default: return 'Análisis Detallado';
    }
  };

  const getDetailIcon = () => {
    switch (detailType) {
      case 'volume': return <Package className="w-5 h-5 text-blue-500" />;
      case 'revenue': return <BarChart3 className="w-5 h-5 text-primary" />;
      case 'cogs': return <BarChart3 className="w-5 h-5 text-destructive" />;
      case 'grossProfit': return <TrendingUp className="w-5 h-5 text-success" />;
      case 'opex': return <BarChart3 className="w-5 h-5 text-warning" />;
      case 'depreciation': return <Building2 className="w-5 h-5 text-slate-400" />;
      case 'interest': return <CreditCard className="w-5 h-5 text-pink-500" />;
      default: return <BarChart3 className="w-5 h-5 text-primary" />;
    }
  };

  const renderDetailContent = () => {
    let data;
    let valueLabel = 'Monto';
    let isVolume = false;
    let isInterest = false;
    let isDepreciation = false;
    let isGrossProfit = false;
    
    switch (detailType) {
      case 'volume':
        data = getVolumeDataAllYears();
        valueLabel = 'Unidades';
        isVolume = true;
        break;
      case 'revenue':
        data = getRevenueDataAllYears();
        valueLabel = 'Ingresos';
        break;
      case 'cogs':
        data = getCogsDataAllYears();
        valueLabel = 'Costos';
        break;
      case 'grossProfit':
        data = getGrossProfitDataAllYears();
        valueLabel = 'Utilidad';
        isGrossProfit = true;
        break;
      case 'opex':
        data = getOpexDataAllYears();
        valueLabel = 'Gastos';
        break;
      case 'depreciation':
        data = getDepreciationDataAllYears();
        valueLabel = 'Depreciación';
        isDepreciation = true;
        break;
      case 'interest':
        data = getInterestDataAllYears();
        valueLabel = 'Intereses';
        isInterest = true;
        break;
      default:
        return <p>Sin datos disponibles</p>;
    }

    const { tableData, chartData, waffleData, amortizationData } = data;

    if (tableData.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <p>No hay datos disponibles para este análisis.</p>
          <p className="text-sm mt-2">Asegúrate de haber ingresado los datos en las secciones anteriores.</p>
        </div>
      );
    }

    const totalByYear = Array.from({ length: numYears }, (_, i) => 
      tableData.reduce((sum, row) => sum + (row[`year${i + 1}`] || row[`profit_year${i + 1}`] || 0), 0)
    );
    const grandTotal = tableData.reduce((sum, row) => sum + (row.total || row.totalProfit || 0), 0);

    // Special rendering for Gross Profit by product
    if (isGrossProfit) {
      return (
        <div className="space-y-6">
          {/* Top: Full width table */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-3">Utilidad Bruta por Producto</h4>
            <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
              <Table className="text-sm">
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow className="bg-secondary/30">
                    <TableHead className="sticky left-0 bg-secondary/30 min-w-32">Producto</TableHead>
                    {Array.from({ length: numYears }, (_, i) => (
                      <TableHead key={i} className="text-center min-w-28">
                        <div>Año {i + 1}</div>
                        <div className="text-xs text-muted-foreground font-normal">Utilidad / Margen</div>
                      </TableHead>
                    ))}
                    <TableHead className="text-center min-w-24 bg-primary/10">Total</TableHead>
                    <TableHead className="text-center min-w-20 bg-primary/10">Margen Prom.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableData.map((row, idx) => (
                    <TableRow key={row.name} className={idx % 2 === 0 ? 'bg-secondary/10' : ''}>
                      <TableCell className="sticky left-0 bg-inherit font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded" style={{ backgroundColor: row.color }} />
                          <span className="truncate max-w-[100px]">{row.name}</span>
                        </div>
                      </TableCell>
                      {Array.from({ length: numYears }, (_, i) => (
                        <TableCell key={i} className="text-center">
                          <div className="tabular-nums font-medium">{formatValue(row[`profit_year${i + 1}`] || 0)}</div>
                          <div className={cn(
                            "text-xs tabular-nums",
                            row[`margin_year${i + 1}`] >= 50 ? "text-success" : 
                            row[`margin_year${i + 1}`] >= 30 ? "text-warning" : "text-destructive"
                          )}>
                            {(row[`margin_year${i + 1}`] || 0).toFixed(1)}%
                          </div>
                        </TableCell>
                      ))}
                      <TableCell className="text-center tabular-nums font-semibold bg-primary/5">
                        {formatValue(row.totalProfit)}
                      </TableCell>
                      <TableCell className={cn(
                        "text-center tabular-nums font-bold bg-primary/5",
                        row.avgMargin >= 50 ? "text-success" : 
                        row.avgMargin >= 30 ? "text-warning" : "text-destructive"
                      )}>
                        {row.avgMargin.toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow className="bg-primary/10 border-t-2 border-primary/30 font-bold">
                    <TableCell className="sticky left-0 bg-primary/10">TOTAL</TableCell>
                    {Array.from({ length: numYears }, (_, i) => {
                      const yearProfit = tableData.reduce((sum, r) => sum + (r[`profit_year${i + 1}`] || 0), 0);
                      const yearRevenue = tableData.reduce((sum, r) => sum + (r[`revenue_year${i + 1}`] || 0), 0);
                      const yearMargin = yearRevenue > 0 ? (yearProfit / yearRevenue) * 100 : 0;
                      return (
                        <TableCell key={i} className="text-center">
                          <div className="tabular-nums">{formatValue(yearProfit)}</div>
                          <div className="text-xs tabular-nums text-muted-foreground">{yearMargin.toFixed(1)}%</div>
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-center tabular-nums bg-primary/20">{formatValue(grandTotal)}</TableCell>
                    <TableCell className="text-center tabular-nums text-success bg-primary/20">
                      {(() => {
                        const totalRev = tableData.reduce((sum, r) => sum + r.totalRevenue, 0);
                        return totalRev > 0 ? ((grandTotal / totalRev) * 100).toFixed(1) : 0;
                      })()}%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Bottom: Chart left, Waffle right */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-3">Utilidad Bruta por Año</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} barGap={1} barCategoryGap="20%" margin={{ top: 20, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                  <XAxis dataKey="year" stroke="#94A3B8" fontSize={10} />
                  <YAxis stroke="#94A3B8" fontSize={9} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.2)]} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142', fontSize: '11px' }}
                    formatter={(value) => formatValue(value)}
                  />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  {tableData.map((item, idx) => (
                    <Bar key={item.name} dataKey={item.name} fill={item.color} stackId="a">
                      {showLabels && idx === tableData.length - 1 && (
                        <LabelList
                          dataKey="_total"
                          position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={9}
                        />
                      )}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <WaffleChart 
              data={waffleData} 
              title="Composición de Utilidad Bruta" 
            />
          </div>

          {/* Ticket Promedio Global - full width */}
          <Card className="bg-secondary/20" data-testid="ticket-promedio-gross-profit">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <LineChart className="w-4 h-4 text-emerald-400" />
                Ticket Promedio Global
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const ticketGlobalData = Array.from({ length: numYears }, (_, i) => {
                  const yr = i + 1;
                  let totalVol = 0, totalRev = 0, totalCogs = 0;
                  products.forEach(p => {
                    const calc = calculateProductYear(p, yr);
                    totalVol += calc.volume;
                    totalRev += calc.revenue;
                    totalCogs += calc.cogs;
                  });
                  return {
                    year: `Año ${yr}`,
                    'Ticket Ingreso': totalVol > 0 ? totalRev / totalVol : 0,
                    'Ticket Costo': totalVol > 0 ? totalCogs / totalVol : 0,
                    'Ticket Utilidad': totalVol > 0 ? (totalRev - totalCogs) / totalVol : 0,
                  };
                });
                return (
                  <div className="space-y-3">
                    <Table className="text-xs">
                      <TableHeader>
                        <TableRow className="bg-secondary/30">
                          <TableHead className="font-bold">Métrica</TableHead>
                          {ticketGlobalData.map((d, i) => (
                            <TableHead key={i} className="text-center font-bold">{d.year}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-semibold text-blue-400">Ticket Ingreso</TableCell>
                          {ticketGlobalData.map((d, i) => (
                            <TableCell key={i} className="text-center tabular-nums text-blue-400">{formatValue(d['Ticket Ingreso'])}</TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-semibold text-red-400">Ticket Costo</TableCell>
                          {ticketGlobalData.map((d, i) => (
                            <TableCell key={i} className="text-center tabular-nums text-red-400">{formatValue(d['Ticket Costo'])}</TableCell>
                          ))}
                        </TableRow>
                        <TableRow className="bg-emerald-500/5 border-t border-emerald-500/30">
                          <TableCell className="font-bold text-emerald-400">Ticket Utilidad</TableCell>
                          {ticketGlobalData.map((d, i) => (
                            <TableCell key={i} className="text-center tabular-nums font-semibold text-emerald-400">{formatValue(d['Ticket Utilidad'])}</TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                    <div style={{ width: '100%', height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={ticketGlobalData} margin={{ top: 15, right: 20, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                          <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} />
                          <YAxis stroke="#94A3B8" fontSize={10} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]} />
                          <Tooltip contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }} formatter={(value) => formatCurrency(value, currency)} />
                          <Legend />
                          <Bar dataKey="Ticket Ingreso" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                            {showLabels && <LabelList dataKey="Ticket Ingreso" position="top" formatter={(v) => formatChartValue(v)} fill="#3B82F6" fontSize={9} />}
                          </Bar>
                          <Bar dataKey="Ticket Costo" fill="#EF4444" radius={[4, 4, 0, 0]}>
                            {showLabels && <LabelList dataKey="Ticket Costo" position="top" formatter={(v) => formatChartValue(v)} fill="#EF4444" fontSize={9} />}
                          </Bar>
                          <Line type="monotone" dataKey="Ticket Utilidad" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981', r: 4 }}>
                            {showLabels && <LabelList dataKey="Ticket Utilidad" position="top" formatter={(v) => formatChartValue(v)} fill="#10B981" fontSize={9} />}
                          </Line>
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      );
    }

    // Special rendering for Interest with amortization table
    if (isInterest && amortizationData) {
      return (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-pink-500/10 to-transparent border-pink-500/30">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Deuda Original</p>
                <p className="text-xl font-bold text-pink-500">{formatValue(debtAmount)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/30">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Tasa de Interés</p>
                <p className="text-xl font-bold text-blue-500">{costOfDebt}%</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-destructive/10 to-transparent border-destructive/30">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Intereses Totales</p>
                <p className="text-xl font-bold text-destructive">{formatValue(grandTotal)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-success/10 to-transparent border-success/30">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Tipo Amortización</p>
                <p className="text-sm font-bold text-success">{monthlyAmortization ? 'Pagos Iguales' : 'Bullet (Al Final)'}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Amortization Table */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground">Tabla de Amortización</h4>
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                <Table className="text-sm">
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow className="bg-secondary/30">
                      <TableHead>Año</TableHead>
                      <TableHead className="text-right">Saldo Inicial</TableHead>
                      <TableHead className="text-right">Intereses</TableHead>
                      <TableHead className="text-right">Capital</TableHead>
                      <TableHead className="text-right">Saldo Final</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {amortizationSchedule.map((row, idx) => (
                      <TableRow key={row.year} className={idx % 2 === 0 ? 'bg-secondary/10' : ''}>
                        <TableCell className="font-medium">Año {row.year}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatValue(row.beginning_balance)}</TableCell>
                        <TableCell className="text-right tabular-nums text-destructive">{formatValue(row.interest)}</TableCell>
                        <TableCell className="text-right tabular-nums text-success">{formatValue(row.principal)}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{formatValue(row.ending_balance)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-primary/10 font-bold">
                      <TableCell>TOTAL</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right tabular-nums text-destructive">{formatValue(grandTotal)}</TableCell>
                      <TableCell className="text-right tabular-nums text-success">{formatValue(debtAmount)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Charts */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-3">Saldo Insoluto vs Intereses</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={amortizationData} margin={{ top: 15, right: 5, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                    <XAxis dataKey="year" stroke="#94A3B8" fontSize={10} />
                    <YAxis yAxisId="left" stroke="#94A3B8" fontSize={9} tickFormatter={(v) => formatChartValue(v)} domain={[0, 'auto']} />
                    <YAxis yAxisId="right" orientation="right" stroke="#94A3B8" fontSize={9} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.2)]} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142', fontSize: '11px' }}
                      formatter={(value) => formatValue(value)}
                    />
                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                    <Area yAxisId="left" type="monotone" dataKey="Saldo Insoluto" fill={CHART_COLORS.balance} fillOpacity={0.3} stroke={CHART_COLORS.balance} strokeWidth={2} />
                    <Bar yAxisId="right" dataKey="Intereses" fill={CHART_COLORS.interest} radius={[2, 2, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-3">Pagos: Capital vs Intereses</h4>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={chartData} margin={{ top: 15, right: 5, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                    <XAxis dataKey="year" stroke="#94A3B8" fontSize={10} />
                    <YAxis stroke="#94A3B8" fontSize={9} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.2)]} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142', fontSize: '11px' }}
                      formatter={(value) => formatValue(value)}
                    />
                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                    <Bar dataKey="Intereses" fill={CHART_COLORS.interest} stackId="a" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Capital" fill={CHART_COLORS.principal} stackId="a" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Special rendering for Depreciation with investment details
    if (isDepreciation) {
      return (
        <div className="space-y-6">
          {/* Top: Full width table */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-3">Depreciación por Activo</h4>
            <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
              <Table className="text-sm">
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow className="bg-secondary/30">
                    <TableHead className="sticky left-0 bg-secondary/30 min-w-32">Activo</TableHead>
                    <TableHead className="text-right min-w-24">Inversión</TableHead>
                    <TableHead className="text-center min-w-16">Vida Útil</TableHead>
                    {Array.from({ length: numYears }, (_, i) => (
                      <TableHead key={i} className="text-center min-w-20">Año {i + 1}</TableHead>
                    ))}
                    <TableHead className="text-center min-w-24 bg-primary/10">Total Dep.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableData.map((row, idx) => (
                    <TableRow key={row.name} className={idx % 2 === 0 ? 'bg-secondary/10' : ''}>
                      <TableCell className="sticky left-0 bg-inherit font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded" style={{ backgroundColor: row.color }} />
                          <span className="truncate max-w-[100px]">{row.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatValue(row.amount)}</TableCell>
                      <TableCell className="text-center">{row.depYears} años</TableCell>
                      {Array.from({ length: numYears }, (_, i) => (
                        <TableCell key={i} className="text-center tabular-nums">
                          {formatValue(row[`year${i + 1}`] || 0)}
                        </TableCell>
                      ))}
                      <TableCell className="text-center tabular-nums font-semibold bg-primary/5">
                        {formatValue(row.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-primary/10 border-t-2 border-primary/30 font-bold">
                    <TableCell className="sticky left-0 bg-primary/10">TOTAL</TableCell>
                    <TableCell className="text-right tabular-nums">{formatValue(tableData.reduce((sum, r) => sum + r.amount, 0))}</TableCell>
                    <TableCell></TableCell>
                    {totalByYear.map((total, i) => (
                      <TableCell key={i} className="text-center tabular-nums">{formatValue(total)}</TableCell>
                    ))}
                    <TableCell className="text-center tabular-nums bg-primary/20">{formatValue(grandTotal)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Bottom: Chart left, Waffle right */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-3">Depreciación Anual por Activo</h4>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} barGap={1} barCategoryGap="20%" margin={{ top: 20, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                  <XAxis dataKey="year" stroke="#94A3B8" fontSize={10} />
                  <YAxis stroke="#94A3B8" fontSize={9} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.2)]} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142', fontSize: '11px' }}
                    formatter={(value) => formatValue(value)}
                  />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  {tableData.map((item, idx) => (
                    <Bar key={item.name} dataKey={item.name} fill={item.color} stackId="a">
                      {showLabels && idx === tableData.length - 1 && (
                        <LabelList
                          dataKey="_total"
                          position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={9}
                        />
                      )}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <WaffleChart 
              data={waffleData} 
              title="Composición de Inversión" 
            />
          </div>
        </div>
      );
    }

    // Default rendering for Volume, Revenue, COGS, OPEX
    return (
      <div className="space-y-6">
        {/* Top: Full width table */}
        <div>
          <h4 className="text-sm font-semibold text-muted-foreground mb-3">Desglose Anual</h4>
          <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
            <Table className="text-sm">
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow className="bg-secondary/30">
                  <TableHead className="sticky left-0 bg-secondary/30 min-w-32">
                    {detailType === 'opex' ? 'Categoría' : 'Producto'}
                  </TableHead>
                  {Array.from({ length: numYears }, (_, i) => (
                    <TableHead key={i} className="text-center min-w-20">Año {i + 1}</TableHead>
                  ))}
                  <TableHead className="text-center min-w-24 bg-primary/10">Total</TableHead>
                  <TableHead className="text-center min-w-20 bg-primary/10">CAGR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData.map((row, idx) => {
                  const cagr = calculateCagr(row.year1, row[`year${numYears}`], numYears - 1);
                  return (
                    <TableRow key={row.name} className={idx % 2 === 0 ? 'bg-secondary/10' : ''}>
                      <TableCell className="sticky left-0 bg-inherit font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded" style={{ backgroundColor: row.color }} />
                          <span className="truncate max-w-[100px]">{row.name}</span>
                        </div>
                      </TableCell>
                      {Array.from({ length: numYears }, (_, i) => (
                        <TableCell key={i} className="text-center tabular-nums">
                          {isVolume 
                            ? formatVolumeValue(row[`year${i + 1}`] || 0)
                            : formatValue(row[`year${i + 1}`] || 0)
                          }
                        </TableCell>
                      ))}
                      <TableCell className="text-center tabular-nums font-semibold bg-primary/5">
                        {isVolume ? formatVolumeValue(row.total) : formatValue(row.total)}
                      </TableCell>
                      <TableCell className={cn(
                        "text-center tabular-nums font-bold bg-primary/5",
                        cagr > 0 ? "text-success" : cagr < 0 ? "text-destructive" : ""
                      )}>
                        {cagr !== null ? `${cagr > 0 ? '+' : ''}${cagr.toFixed(1)}%` : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Total Row */}
                <TableRow className="bg-primary/10 border-t-2 border-primary/30 font-bold">
                  <TableCell className="sticky left-0 bg-primary/10">TOTAL</TableCell>
                  {totalByYear.map((total, i) => (
                    <TableCell key={i} className="text-center tabular-nums">
                      {isVolume ? formatVolumeValue(total) : formatValue(total)}
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums bg-primary/20">
                    {isVolume ? formatVolumeValue(grandTotal) : formatValue(grandTotal)}
                  </TableCell>
                  <TableCell className="text-center tabular-nums text-success bg-primary/20">
                    {(() => {
                      const cagr = calculateCagr(totalByYear[0], totalByYear[numYears - 1], numYears - 1);
                      return cagr !== null ? `+${cagr.toFixed(1)}%` : '-';
                    })()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Bottom: Chart left, Waffle right */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-3">Evolución Anual</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} barGap={1} barCategoryGap="20%" margin={{ top: 20, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                <XAxis dataKey="year" stroke="#94A3B8" fontSize={10} />
                <YAxis stroke="#94A3B8" fontSize={9} tickFormatter={(v) => isVolume ? formatVolumeValue(v) : formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.2)]} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142', fontSize: '11px' }}
                  formatter={(value) => isVolume ? formatVolumeValue(value) : formatValue(value)}
                />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
                {tableData.map((item, idx) => (
                  <Bar key={item.name} dataKey={item.name} fill={item.color} stackId="a">
                    {showLabels && idx === tableData.length - 1 && (
                      <LabelList
                        dataKey="_total"
                        position="top" formatter={(v) => isVolume ? formatVolumeValue(v) : formatChartValue(v)} fill="#94A3B8" fontSize={9}
                      />
                    )}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <WaffleChart 
            data={waffleData} 
            title="Composición Promedio" 
          />
        </div>

        {/* Ticket Promedio Global */}
        {(detailType === 'revenue' || detailType === 'cogs') && products && products.length > 0 && (() => {
          const ticketGlobalData = Array.from({ length: numYears }, (_, i) => {
            const yr = i + 1;
            let totalVol = 0, totalRev = 0, totalCogs = 0;
            products.forEach(p => {
              const calc = calculateProductYear(p, yr);
              totalVol += calc.volume;
              totalRev += calc.revenue;
              totalCogs += calc.cogs;
            });
            const ticketVal = totalVol > 0
              ? (detailType === 'revenue' ? totalRev / totalVol : totalCogs / totalVol)
              : 0;
            return {
              year: `Año ${yr}`,
              ticket: ticketVal,
              volumenTotal: totalVol,
            };
          });
          const t1 = ticketGlobalData[0]?.ticket || 0;
          const tL = ticketGlobalData[ticketGlobalData.length - 1]?.ticket || 0;
          const cagr = calculateCagr(t1, tL, numYears - 1);
          return (
            <Card className="bg-secondary/20" data-testid="ticket-promedio-revenue-cogs">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <LineChart className="w-4 h-4" />
                  {detailType === 'revenue' ? 'Ticket Promedio de Ingreso' : 'Ticket Promedio de Costo'}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  {detailType === 'revenue' ? 'Ingreso total / Volumen total' : 'Costo total / Volumen total'} (promedio ponderado global)
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow className="bg-secondary/30">
                      <TableHead className="font-bold">Métrica</TableHead>
                      {ticketGlobalData.map((d, i) => (
                        <TableHead key={i} className="text-center font-bold">{d.year}</TableHead>
                      ))}
                      <TableHead className="text-center font-bold bg-primary/10">CAGR</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className={cn("font-semibold", detailType === 'revenue' ? "text-blue-400" : "text-red-400")}>
                        {detailType === 'revenue' ? 'Ticket Ingreso' : 'Ticket Costo'}
                      </TableCell>
                      {ticketGlobalData.map((d, i) => (
                        <TableCell key={i} className={cn("text-center tabular-nums font-semibold", detailType === 'revenue' ? "text-blue-400" : "text-red-400")}>
                          {formatValue(d.ticket)}
                        </TableCell>
                      ))}
                      <TableCell className={cn("text-center font-bold bg-primary/5", detailType === 'revenue' ? "text-blue-400" : "text-red-400")}>
                        {cagr !== null ? `${cagr > 0 ? '+' : ''}${cagr.toFixed(1)}%` : '--'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div style={{ width: '100%', height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ticketGlobalData} margin={{ top: 15, right: 20, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                      <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} />
                      <YAxis stroke="#94A3B8" fontSize={10} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]} />
                      <Tooltip contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }} formatter={(value) => formatCurrency(value, currency)} />
                      <Bar dataKey="ticket" fill={detailType === 'revenue' ? '#3B82F6' : '#EF4444'} 
                        name={detailType === 'revenue' ? 'Ticket Ingreso' : 'Ticket Costo'} radius={[4, 4, 0, 0]}>
                        {showLabels && <LabelList dataKey="ticket" position="top" formatter={(v) => formatChartValue(v)} 
                          fill={detailType === 'revenue' ? '#3B82F6' : '#EF4444'} fontSize={9} />}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          );
        })()}
      </div>
    );
  };

  const totalNetIncome = years.reduce((sum, y) => sum + y.net_income, 0);
  const avgEbitdaMargin = years.length > 0 
    ? years.reduce((sum, y) => sum + y.ebitda_margin_pct, 0) / years.length 
    : 0;

  const performanceChartData = years.map(y => ({
    year: `Año ${y.year}`,
    'Ventas': y.revenue,
    'EBITDA': y.ebitda,
    'Utilidad Neta': y.net_income
  }));

  const costChartData = years.map(y => ({
    year: `Año ${y.year}`,
    'COGS': y.cogs,
    'Gastos Op.': y.operating_expenses,
    'Depreciación': y.depreciation,
    'Intereses': y.interest_expense
  }));

  const getFilteredCostData = () => {
    if (costFilter === 'all') return costChartData;
    return costChartData.map(d => ({
      year: d.year,
      [costFilter]: d[costFilter]
    }));
  };

  const getVolumeByYear = (yearNum) => {
    if (!products || products.length === 0) return 0;
    return products.reduce((total, p) => {
      const calc = calculateProductYear(p, yearNum);
      return total + calc.volume;
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando estado de resultados...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="income-statement-page">
      {/* Enhanced Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              {getDetailIcon()}
              {getDetailTitle()}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {renderDetailContent()}
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium">PASO 8 DE 10</span>
          </div>
          <h1 className="text-4xl font-bold">Estado de Resultados</h1>
          <p className="text-muted-foreground">Proyección financiera • Click en cualquier rubro para análisis detallado</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2"
            onClick={() => exportIncomeStatementToExcel(years, currentProject?.name || 'Proyecto', currency)}
            data-testid="export-income-statement-excel-btn">
            <Download className="w-4 h-4" />
            Excel
          </Button>
          <Button variant="outline" className="gap-2">
            <Printer className="w-4 h-4" />
            PDF
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/30">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Utilidad Neta Acumulada</p>
            <p className="text-2xl font-bold">{formatValue(totalNetIncome)}</p>
            <p className={cn("text-xs mt-1", totalNetIncome > 0 ? "text-success" : "text-destructive")}>
              {years.length} años proyectados
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-success/10 to-transparent border-success/30">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Margen EBITDA Promedio</p>
            <p className="text-2xl font-bold">{avgEbitdaMargin.toFixed(1)}%</p>
            <p className="text-xs text-muted-foreground mt-1">Sobre ingresos totales</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/30">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Margen Neto Promedio</p>
            <p className="text-2xl font-bold">
              {years.length > 0 
                ? (years.reduce((sum, y) => sum + getMargin(y.net_income, y.revenue), 0) / years.length).toFixed(1)
                : 0}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">Utilidad / Ingresos</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-2 gap-6">
        {/* Income Statement Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              Estado de Resultados Proyectado
              <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded font-normal">Click para análisis</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table className="financial-table text-sm">
              <TableHeader>
                <TableRow className="bg-secondary/30">
                  <TableHead className="w-48 sticky left-0 bg-secondary/30">CONCEPTO</TableHead>
                  {years.map(y => (
                    <TableHead key={y.year} className="text-center min-w-20">Año {y.year}</TableHead>
                  ))}
                  <TableHead className="text-center min-w-20 bg-primary/20 text-primary">CAGR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Volume Row */}
                <TableRow className="bg-blue-500/5 hover:bg-blue-500/10 cursor-pointer" onClick={() => openDetail('volume')}>
                  <TableCell className="sticky left-0 bg-blue-500/5 font-medium">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-blue-500" />
                      Volumen Total (uds)
                      <Eye className="w-3 h-3 text-blue-500" />
                    </div>
                  </TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums">
                      {formatVolumeValue(getVolumeByYear(y.year))}
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums font-bold text-primary bg-primary/10">
                    {(() => {
                      const first = getVolumeByYear(years[0]?.year);
                      const last = getVolumeByYear(years[years.length - 1]?.year);
                      const cagr = calculateCagr(first, last, years.length - 1);
                      return cagr !== null ? `${cagr > 0 ? '+' : ''}${cagr.toFixed(1)}%` : '-';
                    })()}
                  </TableCell>
                </TableRow>

                {/* Revenue */}
                <TableRow className="hover:bg-primary/5 cursor-pointer" onClick={() => openDetail('revenue')}>
                  <TableCell className="sticky left-0 bg-card font-semibold text-primary">
                    <div className="flex items-center gap-2">
                      Ingresos por Ventas
                      <Eye className="w-3 h-3 text-primary" />
                    </div>
                  </TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums font-semibold">
                      {formatValue(y.revenue)}
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums font-bold text-success bg-primary/10">
                    {getCagr('revenue') !== null ? `+${getCagr('revenue').toFixed(1)}%` : '-'}
                  </TableCell>
                </TableRow>

                {/* COGS */}
                <TableRow className="hover:bg-destructive/5 cursor-pointer" onClick={() => openDetail('cogs')}>
                  <TableCell className="sticky left-0 bg-card">
                    <div className="flex items-center gap-2">
                      Costo de Ventas (COGS)
                      <Eye className="w-3 h-3 text-destructive" />
                    </div>
                  </TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums text-destructive">
                      ({formatValue(y.cogs)})
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums text-destructive bg-primary/10">
                    {getCagr('cogs') !== null ? `+${getCagr('cogs').toFixed(1)}%` : '-'}
                  </TableCell>
                </TableRow>

                {/* Gross Margin - Clickable for Gross Profit analysis */}
                <TableRow className="bg-secondary/20 hover:bg-success/10 cursor-pointer" onClick={() => openDetail('grossProfit')}>
                  <TableCell className="sticky left-0 bg-secondary/20 font-semibold">
                    <div className="flex items-center gap-2">
                      Utilidad Bruta
                      <Eye className="w-3 h-3 text-success" />
                    </div>
                  </TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums font-semibold">
                      {formatValue(y.gross_margin)}
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums font-bold text-success bg-primary/10">
                    {getCagr('gross_margin') !== null ? `+${getCagr('gross_margin').toFixed(1)}%` : '-'}
                  </TableCell>
                </TableRow>
                <TableRow className="bg-secondary/10">
                  <TableCell className="sticky left-0 bg-secondary/10 text-xs text-muted-foreground italic pl-6">% Margen</TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums text-xs text-muted-foreground">
                      {getMargin(y.gross_margin, y.revenue).toFixed(1)}%
                    </TableCell>
                  ))}
                  <TableCell className="bg-primary/10"></TableCell>
                </TableRow>

                {/* OPEX */}
                <TableRow className="hover:bg-warning/5 cursor-pointer" onClick={() => openDetail('opex')}>
                  <TableCell className="sticky left-0 bg-card">
                    <div className="flex items-center gap-2">
                      Gastos Operativos
                      <Eye className="w-3 h-3 text-warning" />
                    </div>
                  </TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums text-warning">
                      ({formatValue(y.operating_expenses)})
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums text-warning bg-primary/10">
                    {getCagr('operating_expenses') !== null ? `+${getCagr('operating_expenses').toFixed(1)}%` : '-'}
                  </TableCell>
                </TableRow>

                {/* EBITDA */}
                <TableRow className="bg-secondary/20">
                  <TableCell className="sticky left-0 bg-secondary/20 font-semibold">EBITDA</TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums font-semibold text-success">
                      {formatValue(y.ebitda)}
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums font-bold text-success bg-primary/10">
                    {getCagr('ebitda') !== null ? `+${getCagr('ebitda').toFixed(1)}%` : '-'}
                  </TableCell>
                </TableRow>
                <TableRow className="bg-secondary/10">
                  <TableCell className="sticky left-0 bg-secondary/10 text-xs text-muted-foreground italic pl-6">% Margen</TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums text-xs text-muted-foreground">
                      {getMargin(y.ebitda, y.revenue).toFixed(1)}%
                    </TableCell>
                  ))}
                  <TableCell className="bg-primary/10"></TableCell>
                </TableRow>

                {/* Depreciation */}
                <TableRow className="hover:bg-slate-500/5 cursor-pointer" onClick={() => openDetail('depreciation')}>
                  <TableCell className="sticky left-0 bg-card">
                    <div className="flex items-center gap-2">
                      Depreciación
                      <Eye className="w-3 h-3 text-slate-400" />
                    </div>
                  </TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums text-muted-foreground">
                      ({formatValue(y.depreciation)})
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums text-muted-foreground bg-primary/10">-</TableCell>
                </TableRow>

                {/* EBIT */}
                <TableRow className="bg-secondary/20">
                  <TableCell className="sticky left-0 bg-secondary/20 font-semibold">EBIT</TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums font-semibold">
                      {formatValue(y.ebit)}
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums font-bold text-success bg-primary/10">
                    {getCagr('ebit') !== null ? `+${getCagr('ebit').toFixed(1)}%` : '-'}
                  </TableCell>
                </TableRow>
                <TableRow className="bg-secondary/10">
                  <TableCell className="sticky left-0 bg-secondary/10 text-xs text-muted-foreground italic pl-6">% Margen</TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums text-xs text-muted-foreground">
                      {getMargin(y.ebit, y.revenue).toFixed(1)}%
                    </TableCell>
                  ))}
                  <TableCell className="bg-primary/10"></TableCell>
                </TableRow>

                {/* Interest */}
                <TableRow className="hover:bg-pink-500/5 cursor-pointer" onClick={() => openDetail('interest')}>
                  <TableCell className="sticky left-0 bg-card">
                    <div className="flex items-center gap-2">
                      Gastos por Intereses
                      <Eye className="w-3 h-3 text-pink-500" />
                    </div>
                  </TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums text-destructive">
                      ({formatValue(y.interest_expense)})
                    </TableCell>
                  ))}
                  <TableCell className="text-center bg-primary/10">-</TableCell>
                </TableRow>

                {/* EBT */}
                <TableRow className="bg-secondary/20">
                  <TableCell className="sticky left-0 bg-secondary/20 font-semibold">UAI (EBT)</TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums font-semibold">
                      {formatValue(y.ebt)}
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums font-bold text-success bg-primary/10">
                    {getCagr('ebt') !== null ? `+${getCagr('ebt').toFixed(1)}%` : '-'}
                  </TableCell>
                </TableRow>
                <TableRow className="bg-secondary/10">
                  <TableCell className="sticky left-0 bg-secondary/10 text-xs text-muted-foreground italic pl-6">% Margen</TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums text-xs text-muted-foreground">
                      {getMargin(y.ebt, y.revenue).toFixed(1)}%
                    </TableCell>
                  ))}
                  <TableCell className="bg-primary/10"></TableCell>
                </TableRow>

                {/* Taxes */}
                <TableRow>
                  <TableCell className="sticky left-0 bg-card">Impuestos ({currentProject?.financing?.tax_rate || 30}%)</TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums text-destructive">
                      ({formatValue(y.taxes)})
                    </TableCell>
                  ))}
                  <TableCell className="text-center bg-primary/10">-</TableCell>
                </TableRow>

                {/* Net Income */}
                <TableRow className="bg-primary/10 border-t-2 border-primary/30">
                  <TableCell className="sticky left-0 bg-primary/10 font-bold text-primary">UTILIDAD NETA</TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className={cn(
                      "text-center tabular-nums font-bold",
                      y.net_income >= 0 ? "text-success" : "text-destructive"
                    )}>
                      {formatValue(y.net_income)}
                    </TableCell>
                  ))}
                  <TableCell className="text-center tabular-nums font-bold text-success bg-primary/20">
                    {getCagr('net_income') !== null ? `+${getCagr('net_income').toFixed(1)}%` : '-'}
                  </TableCell>
                </TableRow>
                <TableRow className="bg-primary/5">
                  <TableCell className="sticky left-0 bg-primary/5 text-xs text-muted-foreground italic pl-6">% Margen Neto</TableCell>
                  {years.map(y => (
                    <TableCell key={y.year} className="text-center tabular-nums text-xs text-muted-foreground">
                      {getMargin(y.net_income, y.revenue).toFixed(1)}%
                    </TableCell>
                  ))}
                  <TableCell className="bg-primary/10"></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Charts */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Ventas, EBITDA y Utilidad Neta
                </CardTitle>
                <Select value={perfFilter} onValueChange={setPerfFilter}>
                  <SelectTrigger className="w-40 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="Ventas">Solo Ventas</SelectItem>
                    <SelectItem value="EBITDA">Solo EBITDA</SelectItem>
                    <SelectItem value="Utilidad Neta">Solo Utilidad Neta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={performanceChartData} barGap={4} barCategoryGap="25%" margin={{ top: 25, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                  <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={10} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.25)]} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                    formatter={(value) => formatValue(value)}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  {(perfFilter === 'all' || perfFilter === 'Ventas') && (
                    <Bar dataKey="Ventas" fill={CHART_COLORS.revenue} radius={[2, 2, 0, 0]}>
                      {showLabels && <LabelList dataKey="Ventas" position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={8} />}
                    </Bar>
                  )}
                  {(perfFilter === 'all' || perfFilter === 'EBITDA') && (
                    <Bar dataKey="EBITDA" fill={CHART_COLORS.ebitda} radius={[2, 2, 0, 0]}>
                      {showLabels && <LabelList dataKey="EBITDA" position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={8} />}
                    </Bar>
                  )}
                  {(perfFilter === 'all' || perfFilter === 'Utilidad Neta') && (
                    <Bar dataKey="Utilidad Neta" fill={CHART_COLORS.netIncome} radius={[2, 2, 0, 0]}>
                      {showLabels && <LabelList dataKey="Utilidad Neta" position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={8} />}
                    </Bar>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-warning" />
                  Estructura de Costos
                </CardTitle>
                <Select value={costFilter} onValueChange={setCostFilter}>
                  <SelectTrigger className="w-36 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="COGS">Solo COGS</SelectItem>
                    <SelectItem value="Gastos Op.">Solo Gastos Op.</SelectItem>
                    <SelectItem value="Depreciación">Solo Deprec.</SelectItem>
                    <SelectItem value="Intereses">Solo Intereses</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={getFilteredCostData()} barGap={4} barCategoryGap="25%" margin={{ top: 25, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                  <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={10} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.25)]} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                    formatter={(value) => formatValue(value)}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  {(costFilter === 'all' || costFilter === 'COGS') && (
                    <Bar dataKey="COGS" fill={CHART_COLORS.cogs} radius={[2, 2, 0, 0]}>
                      {showLabels && <LabelList dataKey="COGS" position="top" formatter={(v) => formatChartValue(v)} fill="#EF4444" fontSize={8} />}
                    </Bar>
                  )}
                  {(costFilter === 'all' || costFilter === 'Gastos Op.') && (
                    <Bar dataKey="Gastos Op." fill={CHART_COLORS.opex} radius={[2, 2, 0, 0]}>
                      {showLabels && <LabelList dataKey="Gastos Op." position="top" formatter={(v) => formatChartValue(v)} fill="#F59E0B" fontSize={8} />}
                    </Bar>
                  )}
                  {(costFilter === 'all' || costFilter === 'Depreciación') && (
                    <Bar dataKey="Depreciación" fill={CHART_COLORS.depreciation} radius={[2, 2, 0, 0]}>
                      {showLabels && <LabelList dataKey="Depreciación" position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={8} />}
                    </Bar>
                  )}
                  {(costFilter === 'all' || costFilter === 'Intereses') && (
                    <Bar dataKey="Intereses" fill={CHART_COLORS.interest} radius={[2, 2, 0, 0]}>
                      {showLabels && <LabelList dataKey="Intereses" position="top" formatter={(v) => formatChartValue(v)} fill="#8B5CF6" fontSize={8} />}
                    </Bar>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={goToPrev} className="gap-2">
          <ChevronLeft className="w-4 h-4" />
          Gastos Fijos
        </Button>
        <Button onClick={goToNext} className="gap-2">
          Flujo de Efectivo
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
